import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, Search, RefreshCw, ChevronLeft, ChevronRight, Eye, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';

const FormSubmissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ submissions: [], form: null, total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, [id, page, perPage]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/forms/${id}/submissions?page=${page}&per_page=${perPage}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load submissions');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      await api.delete(`/forms/${id}/submissions/${submissionId}`);
      setData({
        ...data,
        submissions: data.submissions.filter(s => s.submission_id !== submissionId),
        total: data.total - 1
      });
      toast.success('Submission deleted');
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/forms/${id}/submissions/export`);
      const { headers, rows, form_name } = response.data;
      
      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${form_name.replace(/\s+/g, '_')}_submissions.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Submissions exported');
    } catch (error) {
      toast.error('Failed to export submissions');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getFieldLabel = (fieldId) => {
    const field = data.form?.fields?.find(f => f.field_id === fieldId);
    return field?.label || fieldId;
  };

  const formatValue = (value) => {
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value || '-');
  };

  const renderForwarded = (submission) => {
    const notes = submission.notifications;
    if (!Array.isArray(notes) || notes.length === 0) {
      return <span className="text-slate-400">Not forwarded</span>;
    }
    return (
      <div className="space-y-1">
        {notes.map((n, i) => (
          <div key={i} className="flex flex-col">
            <span className="flex items-center gap-1.5">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${n.status === 'sent' ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span className="text-slate-800 truncate max-w-[200px]" title={n.recipient}>{n.recipient}</span>
            </span>
            <span className="text-xs text-slate-400 ml-3">
              {n.status === 'sent' ? formatDate(n.sent_at) : `Failed${n.error ? ': ' + n.error : ''}`}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Filter submissions by search
  const filteredSubmissions = data.submissions.filter(sub => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return Object.values(sub.data || {}).some(v => 
      String(v).toLowerCase().includes(searchLower)
    );
  });

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/forms')}>
              <ArrowLeft size={18} className="mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {data.form?.name || 'Form'} Submissions
              </h1>
              <p className="text-sm text-slate-500">{data.total} total submissions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchSubmissions}>
              <RefreshCw size={18} className="mr-2" /> Refresh
            </Button>
            <Button variant="outline" onClick={() => navigate(`/forms/${id}`)}>
              Edit Form
            </Button>
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700">
              <Download size={18} className="mr-2" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search submissions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 / page</SelectItem>
              <SelectItem value="20">20 / page</SelectItem>
              <SelectItem value="50">50 / page</SelectItem>
              <SelectItem value="100">100 / page</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Submissions Table */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading submissions...</div>
        ) : filteredSubmissions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="text-4xl mb-4">📭</div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {search ? 'No matching submissions' : 'No submissions yet'}
              </h3>
              <p className="text-slate-500 text-center max-w-md">
                {search ? 'Try adjusting your search' : 'Submissions will appear here when visitors fill out your form'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Submitted</th>
                      {(data.form?.fields || []).slice(0, 4).map(field => (
                        <th key={field.field_id} className="text-left px-4 py-3 text-sm font-medium text-slate-600">
                          {field.label}
                        </th>
                      ))}
                      {(data.form?.fields?.length || 0) > 4 && (
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">...</th>
                      )}
                      <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Forwarded to</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.map((submission) => (
                      <tr key={submission.submission_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {formatDate(submission.submitted_at)}
                        </td>
                        {(data.form?.fields || []).slice(0, 4).map(field => (
                          <td key={field.field_id} className="px-4 py-3 text-sm text-slate-900">
                            <span className="truncate max-w-xs block">
                              {formatValue(submission.data?.[field.field_id])}
                            </span>
                          </td>
                        ))}
                        {(data.form?.fields?.length || 0) > 4 && (
                          <td className="px-4 py-3 text-sm text-slate-500">...</td>
                        )}
                        <td className="px-4 py-3 text-sm text-slate-600" data-testid={`submission-forwarded-${submission.submission_id}`}>
                          {renderForwarded(submission)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedSubmission(submission)}>
                            <Eye size={16} className="mr-1" /> View
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(submission.submission_id)}>
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-500">
                  Showing {(page - 1) * perPage + 1} - {Math.min(page * perPage, data.total)} of {data.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <ChevronLeft size={16} /> Previous
                  </Button>
                  <span className="px-3 text-sm text-slate-600">
                    Page {page} of {data.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === data.total_pages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Submission Detail Modal */}
        <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Submission Details</DialogTitle>
            </DialogHeader>
            {selectedSubmission && (
              <div className="space-y-4">
                <div className="text-sm text-slate-500">
                  Submitted on {formatDate(selectedSubmission.submitted_at)}
                </div>
                <div className="divide-y divide-slate-100">
                  {(data.form?.fields || []).map(field => (
                    <div key={field.field_id} className="py-3">
                      <div className="text-sm font-medium text-slate-600">{field.label}</div>
                      <div className="text-slate-900 mt-1">
                        {formatValue(selectedSubmission.data?.[field.field_id])}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-2 border-t">
                  <div className="text-sm font-medium text-slate-600 mb-2">Email forwarding</div>
                  {renderForwarded(selectedSubmission)}
                </div>
                {selectedSubmission.ip_address && (
                  <div className="text-xs text-slate-400 pt-2 border-t">
                    IP: {selectedSubmission.ip_address}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FormSubmissions;
